import { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

// Translation dictionary — English + Italian for client-facing pages.
const DICT = {
  // Header
  "header.login": { en: "Login", it: "Accedi" },
  "header.signup": { en: "Sign Up", it: "Registrati" },
  "header.dashboard": { en: "Dashboard", it: "Pannello" },
  "header.admin": { en: "Admin Panel", it: "Admin" },
  "header.logout": { en: "Logout", it: "Esci" },

  // Landing hero
  "landing.badge": { en: "Licensed Crypto Recovery Firm", it: "Società Autorizzata di Recupero Crypto" },
  "landing.title1": { en: "You lost it.", it: "L'hai persa." },
  "landing.title2": { en: "We bring it", it: "Noi la" },
  "landing.title3": { en: "back.", it: "recuperiamo." },
  "landing.subtitle": {
    en: "Crypto Asset Recovery specializes in retrieving cryptocurrency stolen through scams, phishing, and fraudulent exchanges. Discreet, forensic-grade blockchain investigation paired with legal-track escrow.",
    it: "Crypto Asset Recovery è specializzata nel recupero di criptovalute rubate tramite truffe, phishing e exchange fraudolenti. Indagini forensi discrete sulla blockchain unite a custodia legale.",
  },
  "landing.stat.recovered": { en: "Recovered", it: "Recuperati" },
  "landing.stat.cases": { en: "Cases Closed", it: "Casi Chiusi" },
  "landing.stat.success": { en: "Success Rate", it: "Tasso di Successo" },

  // Signup card
  "signup.tag": { en: "Start Case", it: "Apri Pratica" },
  "signup.title": { en: "Open your file", it: "Apri la tua pratica" },
  "signup.subtitle": { en: "Create your secure client account.", it: "Crea il tuo account cliente sicuro." },
  "signup.submit": { en: "Create Account", it: "Crea Account" },
  "signup.submitting": { en: "Creating...", it: "Creazione..." },
  "signup.hasAccount": { en: "Already a client?", it: "Sei già cliente?" },
  "signup.loginLink": { en: "Log in", it: "Accedi" },

  // Fields
  "field.firstName": { en: "First Name", it: "Nome" },
  "field.lastName": { en: "Last Name", it: "Cognome" },
  "field.email": { en: "Email", it: "Email" },
  "field.password": { en: "Password", it: "Password" },
  "field.phone": { en: "Phone Number", it: "Numero di Telefono" },
  "field.country": { en: "Country", it: "Paese" },

  // Process
  "process.tag": { en: "/ 001 — Process", it: "/ 001 — Processo" },
  "process.title": { en: "Three stages to recovery.", it: "Tre fasi per il recupero." },
  "process.subtitle": {
    en: "Every case follows a proven forensic pipeline used by top-tier blockchain investigation units.",
    it: "Ogni caso segue una pipeline forense collaudata usata dalle migliori unità di indagine blockchain.",
  },
  "process.trace.title": { en: "Trace Services", it: "Servizi di Tracciamento" },
  "process.trace.desc": {
    en: "On-chain forensics to identify wallets, mixers, and exchange endpoints where your funds landed.",
    it: "Indagini on-chain per identificare wallet, mixer ed exchange dove sono finiti i tuoi fondi.",
  },
  "process.freeze.title": { en: "Freeze", it: "Congelamento" },
  "process.freeze.desc": {
    en: "Legal notices filed with custodial exchanges to freeze assets before they are cashed out.",
    it: "Diffide legali inviate agli exchange custodi per congelare gli asset prima che vengano incassati.",
  },
  "process.return.title": { en: "Return", it: "Restituzione" },
  "process.return.desc": {
    en: "Funds are recovered to your Crypto Asset Recovery escrow wallet, then released to your bank or crypto address.",
    it: "I fondi vengono recuperati nel wallet escrow Crypto Asset Recovery, poi trasferiti sulla tua banca o wallet.",
  },

  // Trust grid
  "trust.custody.title": { en: "Bank-grade custody", it: "Custodia di livello bancario" },
  "trust.custody.desc": {
    en: "Recovered funds sit in segregated multi-sig cold storage until you sign for release.",
    it: "I fondi recuperati sono custoditi in cold storage multi-firma finché non autorizzi il rilascio.",
  },
  "trust.dashboard.title": { en: "Live case dashboard", it: "Pannello caso in tempo reale" },
  "trust.dashboard.desc": {
    en: "Track deposits, balances, and live crypto prices from your personal client portal.",
    it: "Monitora depositi, saldo e prezzi crypto in tempo reale dal tuo portale personale.",
  },

  // Return modal
  "return.title": { en: "Where do you want your funds?", it: "Dove vuoi ricevere i fondi?" },
  "return.subtitle": {
    en: "Once recovered, we release your crypto to any of the trusted exchanges below.",
    it: "Una volta recuperati, rilasciamo le tue crypto su uno degli exchange qui sotto.",
  },

  // Login page
  "login.tag": { en: "/ Client Access", it: "/ Accesso Cliente" },
  "login.title": { en: "Log in.", it: "Accedi." },
  "login.subtitle": { en: "Secure access to your recovery file.", it: "Accesso sicuro alla tua pratica di recupero." },
  "login.submit": { en: "Log in", it: "Accedi" },
  "login.submitting": { en: "Signing in...", it: "Accesso in corso..." },
  "login.noAccount": { en: "No account?", it: "Non hai un account?" },
  "login.signupLink": { en: "Open a case", it: "Apri una pratica" },

  // Client Dashboard
  "cd.portal": { en: "/ Client Portal", it: "/ Portale Cliente" },
  "cd.welcome": { en: "Welcome,", it: "Benvenuto," },
  "cd.verified": { en: "Account Verified", it: "Account Verificato" },
  "cd.unverified": { en: "Account Unverified", it: "Account Non Verificato" },
  "cd.deposit": { en: "Deposit", it: "Deposita" },
  "cd.withdraw": { en: "Withdraw", it: "Preleva" },
  "cd.balance": { en: "Recovered Balance", it: "Saldo Recuperato" },
  "cd.balanceHelp": {
    en: "Balance updates when your recovered funds are credited to your account.",
    it: "Il saldo si aggiorna quando i fondi recuperati vengono accreditati sul tuo account.",
  },
  "cd.btcLive": { en: "BTC Live", it: "BTC Live" },
  "cd.ethLive": { en: "ETH Live", it: "ETH Live" },
  "cd.depositWallet": { en: "Deposit Wallet", it: "Wallet di Deposito" },
  "cd.depositTitle": { en: "Send BTC here to fund your case", it: "Invia BTC qui per finanziare la tua pratica" },
  "cd.copy": { en: "Copy", it: "Copia" },
  "cd.copiedToast": { en: "to clipboard", it: "negli appunti" },
  "cd.walletWarning": {
    en: "Only send Bitcoin (BTC). Other networks will result in loss of funds.",
    it: "Invia solo Bitcoin (BTC). Altre reti causeranno la perdita dei fondi.",
  },
  "cd.marketSnapshot": { en: "Market Snapshot", it: "Panoramica Mercato" },
  "cd.txLog": { en: "Transaction Log", it: "Registro Transazioni" },
  "cd.noTx": { en: "No transactions yet.", it: "Nessuna transazione." },
  "cd.balanceRow": { en: "Balance", it: "Saldo" },

  // Deposit modal
  "dep.title": { en: "Send BTC to fund your case", it: "Invia BTC per finanziare la pratica" },
  "dep.address": { en: "BTC Wallet Address", it: "Indirizzo Wallet BTC" },
  "dep.copyBtn": { en: "Copy address", it: "Copia indirizzo" },

  // Withdraw modal
  "wd.title": { en: "Withdraw funds", it: "Preleva fondi" },
  "wd.amount": { en: "Amount (EUR)", it: "Importo (EUR)" },
  "wd.available": { en: "Available:", it: "Disponibile:" },
  "wd.submit": { en: "Withdraw", it: "Preleva" },
  "wd.processing": { en: "Processing…", it: "Elaborazione…" },
  "wd.verificationRequired": { en: "Verification required", it: "Verifica richiesta" },
  "wd.taxLabel": { en: "Traceability", it: "Tracciabilità" },
  "wd.blockedNotice": {
    en: "Account blocked / partial withdrawal unavailable",
    it: "Account bloccato / prelievo parziale non disponibile",
  },
  "wd.mustDeposit1": { en: "You must deposit", it: "Devi depositare" },
  "wd.mustDeposit2": { en: "to unlock your withdrawal of", it: "per sbloccare il tuo prelievo di" },
  "wd.noTax": {
    en: "No tax applies. Contact support to process this withdrawal.",
    it: "Nessuna tassa applicabile. Contatta il supporto per elaborare il prelievo.",
  },
  "wd.depositNow": { en: "Deposit Now", it: "Deposita Ora" },
  "wd.back": { en: "Back", it: "Indietro" },
  // Unverified message translation
  "wd.unverifiedNotice": {
    en: "Your account is not yet verified. To activate it and unlock recovery services, please make a deposit from your personal exchange wallet to link it with the wallet address shown on your dashboard.",
    it: "Il tuo account non è ancora verificato. Per attivare e sbloccare i servizi di recupero effettua un versamento dal tuo wallet exchange personale per unirlo all'indirizzo del portafoglio che trovi nella tua dashboard.",
  },
  "wd.unverifiedTitle": {
    en: "Verify your account",
    it: "Verifica il tuo account",
  },

  // Unverified modal
  "unv.tag": { en: "/ Account Unverified", it: "/ Account Non Verificato" },
  "unv.title": { en: "Activate your account", it: "Attiva il tuo account" },
  "unv.body1": {
    en: "Your account is not yet verified. To activate your case and unlock recovery services,",
    it: "Il tuo account non è ancora verificato. Per attivare la tua pratica e sbloccare i servizi di recupero,",
  },
  "unv.body2": { en: "please make a deposit", it: "effettua un deposito" },
  "unv.body3": {
    en: "to the wallet address on your dashboard.",
    it: "all'indirizzo wallet nel tuo pannello.",
  },
  "unv.cta": { en: "Deposit to activate", it: "Deposita per attivare" },

  // Landing Live Chart section
  "chart.tag": { en: "/ 003 — Live Chart", it: "/ 003 — Grafico Live" },
  "chart.title": {
    en: "Track the market in real time.",
    it: "Monitora il mercato in tempo reale.",
  },
  "chart.subtitle": {
    en: "Real Bitcoin, Ethereum, Solana and XRP prices in euros. Switch between 24H, 7D and 30D views to see where the market is heading.",
    it: "Prezzi reali di Bitcoin, Ethereum, Solana e XRP in euro. Passa tra 24H, 7D e 30G per vedere dove sta andando il mercato.",
  },

  // EUR ↔ Crypto Converter (Landing)
  "conv.tag": { en: "/ 004 — Live Rates", it: "/ 004 — Prezzi Live" },
  "conv.title": { en: "EUR ↔ Crypto converter", it: "Convertitore EUR ↔ Crypto" },
  "conv.subtitle": {
    en: "Instantly translate lost funds into their current euro value using live market rates.",
    it: "Converti istantaneamente i fondi persi nel loro valore attuale in euro con quotazioni in tempo reale.",
  },
  "conv.you.eur": { en: "You have (EUR)", it: "Hai (EUR)" },
  "conv.you.crypto": { en: "You have", it: "Hai" },
  "conv.get.eur": { en: "You get (EUR)", it: "Ricevi (EUR)" },
  "conv.get.crypto": { en: "You get", it: "Ricevi" },
  "conv.flip": { en: "Flip conversion direction", it: "Inverti la conversione" },
  "conv.loading": { en: "Loading rate…", it: "Caricamento…" },
  "conv.footnote": {
    en: "Rates cached for 5 min · Data via Coinbase & CoinGecko",
    it: "Prezzi in cache per 5 min · Dati da Coinbase e CoinGecko",
  },

  // Knowledge Center (Client Dashboard)
  "kc.tag": { en: "/ Knowledge Center", it: "/ Centro Risorse" },
  "kc.title": { en: "Resources for recovering your crypto.", it: "Risorse per recuperare le tue crypto." },
  "kc.subtitle": {
    en: "In-depth guides on password recovery, wallet reviews, and DeFi safety — everything you need to understand crypto ownership and wallet recovery.",
    it: "Guide approfondite su recupero password, recensioni di wallet e sicurezza DeFi — tutto ciò che serve per capire la gestione delle crypto e il recupero dei wallet.",
  },
  "kc.all": { en: "View all", it: "Tutti" },
  "kc.read": { en: "Read article", it: "Leggi l'articolo" },

  // Footer
  "footer.copyright": { en: "© {year} Crypto Asset Recovery", it: "© {year} Crypto Asset Recovery" },
  "footer.compliance": {
    en: "SEC filed · FinCEN registered · GDPR compliant",
    it: "SEC · FinCEN · GDPR",
  },
};

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("cr_lang") || "");
  const [ready, setReady] = useState(!!localStorage.getItem("cr_lang"));

  useEffect(() => {
    if (lang) {
      setReady(true);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/i18n/detect");
        const l = data?.lang === "it" ? "it" : "en";
        setLang(l);
        localStorage.setItem("cr_lang", l);
      } catch {
        setLang("en");
        localStorage.setItem("cr_lang", "en");
      } finally {
        setReady(true);
      }
    })();
  }, [lang]);

  const setLanguage = (l) => {
    const nl = l === "it" ? "it" : "en";
    setLang(nl);
    localStorage.setItem("cr_lang", nl);
  };

  const t = (key, vars = {}) => {
    const entry = DICT[key];
    if (!entry) return key;
    let out = entry[lang] || entry.en || key;
    Object.entries(vars).forEach(([k, v]) => {
      out = out.replace(`{${k}}`, String(v));
    });
    return out;
  };

  return (
    <LangContext.Provider value={{ lang: lang || "en", ready, setLanguage, t }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
